// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its SC_he intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import { checkoutPages } from "@sc/XDL/constants";
import { parseBoolean } from "@sc/XDL/Utilities";
import React, { FunctionComponent } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;

    return {
        cart,
        // XNG-Change
        cartNotPriced: cart?.cartNotPriced,
        hasInsufficientInventory: cart?.hasInsufficientInventory,
        hasInvalidPrice: hasProductsWithInvalidPrice(cart),
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory,
        pickUpAndItemOutOfStock: cart?.properties["pickUpAndItemOutOfStock"],
        cartHasItemsWithoutPriceOrRequireQuote: cart?.properties["cartHasItemsWithoutPriceOrRequireQuote"],
        isSpecialOrderRequested: cart?.properties["isSpecialOrderRequestedForOutOfStockItems"],
        creditHold: cart?.properties.onCreditHold,
        hasItemsWithNoPriceFound: cart?.properties.hasItemsWithNoPriceFound,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        hasQuoteRequiredItems: cart?.cartLines?.some(line => line.quoteRequired),
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    // XNG-Change
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    creditHold,
    isCheckoutPage,
    hasItemsWithNoPriceFound,
    hasQuoteRequiredItems,
}: Props) => {
    // XNG-Changes
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const cartLines = cart.cartLines || [];

    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);

    // XNG-Changes
    return (
        <>
            {cart.isAwaitingApproval && (
                <Notify showClose={false} type="info" fullWidth data-test-selector="cart_approvingOrderMessage">
                    {translate("You are approving order #{0} for {1}", cart.orderNumber, cart.initiatedByUserName)}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth
                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth
                    data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                >
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                >
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify showClose={false} type="warning" fullWidth>
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify showClose={false} type="warning" fullWidth>
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
            {!isCheckoutPage && parseBoolean(creditHold) && (
                <Notify showClose={false} type="warning" fullWidth>
                    {siteMessage("Custom_SC_Cart_CreditHold")}
                </Notify>
            )}
            {!isCheckoutPage && parseBoolean(hasItemsWithNoPriceFound) && !hasQuoteRequiredItems && (
                <Notify showClose={false} type="warning" fullWidth>
                    {siteMessage("Custom_SC_Cart_hasItemsWithNoPriceFound")}
                </Notify>
            )}
            {hasQuoteRequiredItems && (
                <Notify showClose={false} type="warning" fullWidth>
                    {siteMessage("Custom_SC_Cart_hasQuoteRequiredItems")}
                </Notify>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
